/** @jsx jsx */

import { parseSelectionAnswers } from '../common/form/FormikHelper'

export const parsePercentGroupAnswers = (values, template) => {
  const { questionIsPercentGroupSection, questions, currentStep } = template
  const questionsAndAnswers = []
  if (questionIsPercentGroupSection) {
    const oldRetirementAnswer = questions[currentStep]?.[0]?.answers.find(
      answer => answer.id === '29'
    )
    const currentRetirementAnswerValue = values['29']
    if (
      oldRetirementAnswer &&
      oldRetirementAnswer.answerValue !== currentRetirementAnswerValue
    ) {
      const oldValue = oldRetirementAnswer.userSelectedAnswer
        ? parseInt(
            oldRetirementAnswer.answerValue.substring(
              0,
              oldRetirementAnswer.answerValue.length
            )
          )
        : 0
      const currentValue = currentRetirementAnswerValue
        ? parseInt(
            currentRetirementAnswerValue.substring(
              0,
              currentRetirementAnswerValue.length
            )
          )
        : 0
      if (
        (oldValue < 25 && currentValue >= 25) ||
        (oldValue >= 25 && currentValue < 25)
      ) {
        // if the user's new answer would change the following questions in the section, we need to reset those answer values
        questions.forEach((questionSection, sectionIndex) => {
          if (sectionIndex > 0) {
            questionSection.forEach(question => {
              questionsAndAnswers.push({
                questionId: question.id,
                answers: [],
              })
            })
          }
        })
      }
    }
  }
  const answerIds = Object.keys(values)
  if (answerIds.length) {
    const answers = Object.keys(values).map(answerId => {
      return { answerId, answerValue: values[answerId] }
    })
    const question = questions[currentStep]?.find(question =>
      question.answers?.some(answer => answer.id === answers[0]?.answerId)
    )
    if (question) {
      questionsAndAnswers.push({
        questionId: question.id,
        answers,
      })
    }
  }
  return questionsAndAnswers
}

export const parseAnswers = (values, template) => {
  const { questionIsPercentGroupSection, questions, currentStep } = template
  if (questionIsPercentGroupSection) {
    return parsePercentGroupAnswers(values, template)
  }
  const selectionAnswers = {}
  const writeInAnswers = []
  const textAnswers = []
  let inlinePercentAnswers = []
  Object.keys(values).forEach(uniqueId => {
    const question = questions[currentStep]?.find(
      question => question.id === uniqueId
    )
    if (
      question?.answers[0].answerType === 'textarea' ||
      question?.answers[0].answerType === 'text'
    ) {
      textAnswers.push({
        questionId: question.id,
        answers: [
          {
            answerId: question?.answers[0]?.id,
            answerValue: values[uniqueId],
          },
        ],
      })
    } else {
      if (question) {
        // if clearing a selection that previously had a value, we need to update that value to null
        selectionAnswers[uniqueId] = values[uniqueId]?.length
          ? values[uniqueId]
          : null
      } else {
        // percent inputs and write-in answers within selection groups are mapped to answer id
        // if we are here we have one of those
        const foundQuestionFromAnswer = questions[currentStep]?.find(question =>
          question.answers?.some(answer => answer.id === uniqueId)
        )
        // if it is a write in answer, the first answer type will be a radio or checkbox
        if (
          foundQuestionFromAnswer?.answers[0]?.answerType === 'radio' ||
          foundQuestionFromAnswer?.answers[0]?.answerType === 'checkbox'
        ) {
          const answerIsCheckbox =
            foundQuestionFromAnswer?.answers[0]?.answerType === 'checkbox'

          // write in textfields are always preceeded by their corresponding radio or checkbox
          const textFieldIndex = foundQuestionFromAnswer?.answers.findIndex(
            answer => answer.id === uniqueId
          )
          const writeInOption =
            foundQuestionFromAnswer?.answers[textFieldIndex - 1]
          const writeInOptionSelected = answerIsCheckbox
            ? values[foundQuestionFromAnswer.id].findIndex(
                answerId => answerId === writeInOption?.id
              ) > -1
            : writeInOption?.id === values[foundQuestionFromAnswer.id]

          // if the user selected the answer associated with a write in option, we need to store the text value.
          if (writeInOptionSelected) {
            writeInAnswers.push({
              questionId: foundQuestionFromAnswer.id,
              answer: {
                answerId: uniqueId,
                answerValue: values[uniqueId],
              },
            })
          }
        } else {
          if (!inlinePercentAnswers.length) {
            const percentValues = {}
            foundQuestionFromAnswer?.answers.forEach(answer => {
              if (values[answer.id]) {
                percentValues[answer.id] = values[answer.id]
              }
            })
            inlinePercentAnswers = parsePercentGroupAnswers(
              percentValues,
              template
            )
          }
        }
      }
    }
  })

  const parsedSelectionAnswers = parseSelectionAnswers(selectionAnswers)

  if (writeInAnswers.length) {
    writeInAnswers.forEach(writeIn => {
      const selectionAnswer = parsedSelectionAnswers.find(
        answer => answer.questionId === writeIn.questionId
      )
      if (selectionAnswer) {
        selectionAnswer.answers.push(writeIn.answer)
      }
    })
  }
  return [...parsedSelectionAnswers, ...textAnswers, ...inlinePercentAnswers]
}

export const GetPercentGroupHeaders = question => {
  let headers = []
  if (question && question.answers.length) {
    //process answer format: a. Advisory revenue (PPS programs and advisory services): Current %
    if (question.answers[0]?.text?.indexOf(':') !== -1) {
      headers.push(question.answers[0]?.text?.split(':')[1])
    } else {
      //process answer format: Current %
      headers.push(question.answers[0]?.text)
    }
    const desiredHeader = question.answers[1]?.text
    headers.push(desiredHeader)
  }
  return headers
}

export const formatPercentAnswerPair = (question, answer) => {
  if (question.answers.length > 2) {
    const formattedAnswerText = answer.text.substring(
      answer.text.indexOf('. ') + 1,
      answer.text.indexOf(': Current')
    )
    return `${formattedAnswerText}: Current ${
      answer.answerValue ? answer.answerValue : '0%'
    }; Desired ${
      question.answers[answer.order]?.answerValue
        ? question.answers[answer.order]?.answerValue
        : '0%'
    }`
  }
  return `Current ${answer.answerValue ? answer.answerValue : '0%'}; Desired ${
    question.answers[answer.order]?.answerValue
      ? question.answers[answer.order]?.answerValue
      : '0%'
  }`
}

export const formatQuestionText = question => {
  const { text } = question
  const questionText = text.substring(text.indexOf('. ') + 1, text.length)
  // if we are in Business and Target Market and the question is for secondary or tertiary client, we need to specify
  // the target market in the label
  if (question.templateId === '2' && parseInt(question.sectionId) > 3) {
    // secondary market is always sectionId '4', tertiary is '5'
    return `Target Market ${parseInt(question.sectionId) - 2}: ${questionText}`
  }
  return questionText
}

export const filterMarketingObjects = (title, marketingObjects, questions) => {
  const questionIndex = {
    'Marketing Objectives': 0,
    'Resource Plan': 11,
  }

  const questionToCheck = questions[questionIndex[title]]

  const answersToCheck = questionToCheck?.answers?.filter(
    answer => answer.userSelectedAnswer
  )

  if (answersToCheck.length) {
    const filtered = marketingObjects
      .filter(card => {
        const answerIndex = answersToCheck.findIndex(answer =>
          answer.text.includes(card.answerText)
        )
        if (answerIndex > -1) {
          card.answerId = parseInt(answersToCheck[answerIndex].id)
          return card
        }
      })
      .sort((a, b) => a.answerId - b.answerId)
    return filtered.length > 3 ? filtered.slice(0, 3) : filtered
  }
  return []
}

export const resourcePlanQuestionsToCheck = (questions, title) => {
  const questionIndex = {
    'Resource Plan': 11,
  }
  const questionToCheck = questions[questionIndex[title]]

  const answersToCheck = questionToCheck?.answers?.filter(
    answer => answer.userSelectedAnswer
  )
  return answersToCheck && answersToCheck.length
    ? answersToCheck.map(answer => {
        return answer.text
      })
    : []
}
